import { klona } from 'klona';
import { timecodeToSeconds, secondsToTimecode } from './timecodeUtils';

/**
 * Enrich cuesheet items with computed timeline fields.
 * Clones each item via klona so all original API fields are preserved.
 *
 * @param {Array} cueSheetItems - Array of cuesheet items from the API
 * @param {Object} cachedAudioUrls - Map of clip IDs to cached blob URLs
 * @returns {Array} Enriched items ready for TimelineEditor
 */
export function enrichCueSheetItems(cueSheetItems, cachedAudioUrls = {}) {
    if (!Array.isArray(cueSheetItems)) {
        console.warn('enrichCueSheetItems: expected array, got:', typeof cueSheetItems);
        return [];
    }

    return cueSheetItems
        .filter(item => item.timecode != null)
        .map(item => {
        const clone = klona(item);

        const start = timecodeToSeconds(item.timecode || '00:00:00:00', 24);
        const duration = (item.instanceDuration || 5000) / 1000;
        const actualDuration = item.clip?.duration != null ? item.clip.duration / 1000 : duration;

        return {
            ...clone,
            // Computed timeline fields
            layer: item.cueType,                          // 'MUSIC' | 'SFX' | 'MIX'
            start,                                        // seconds
            duration,                                     // seconds
            startPlayback: item.startPlayback ?? 0,       // trim-in offset in seconds
            actualDuration,                               // full clip length in seconds
            displayDuration: duration,
            audioUrl: cachedAudioUrls[item.clip?.id] ?? null,
            draggable: !item.readOnly,
            priority: item.priority ?? 0,                 // overlap order within layer
            // Aliases used by TimelineEditor internals
            filename: item.clip?.name || item.title || 'Untitled',
        };
    });
}

/**
 * @deprecated Use enrichCueSheetItems instead.
 */
export const cueSheetItemsToTracks = enrichCueSheetItems;

/**
 * Returns the next priority value for a new track in a given layer.
 * @param {Array} tracks - Current enriched track list
 * @param {string} layer - Layer ID ('MUSIC' | 'SFX' | 'MIX')
 * @returns {number}
 */
export function getNextPriorityForLayer(tracks, layer) {
    const layerTracks = tracks.filter(t => t.layer === layer);
    if (layerTracks.length === 0) return 0;
    return Math.max(...layerTracks.map(t => t.priority ?? 0)) + 1;
}

/**
 * Compute overlapping track IDs per track (within the same layer only).
 * @param {Array} tracks
 * @returns {Map<string|number, Array<string|number>>}
 */
export function computeOverlaps(tracks) {
    const map = new Map();
    tracks.forEach(a => {
        const overlapping = tracks.filter(b =>
            b.id !== a.id &&
            b.layer === a.layer &&
            a.start < b.start + b.duration &&
            a.start + a.duration > b.start
        );
        map.set(a.id, overlapping.map(b => b.id));
    });
    return map;
}

/**
 * Returns effective volume for a track, muting it when a higher-priority
 * track in the same layer covers the current playback time.
 *
 * @param {Object} track - The track to evaluate
 * @param {Array} allTracks - All tracks in the timeline
 * @param {number} currentTime - Current playback time in seconds
 * @param {number} baseVolume - Base volume from layer/general mix (0–1)
 * @returns {number} 0 if muted by overlap, baseVolume otherwise
 */
export function getTrackVolumeAtTime(track, allTracks, currentTime, baseVolume) {
    const trackIndex = allTracks.findIndex(t => t.id === track.id);
    const trackPriority = track.priority ?? 0;

    const muted = allTracks.some((other, otherIndex) => {
        if (other.id === track.id) return false;
        if (other.layer !== track.layer) return false;
        if (currentTime < other.start || currentTime >= other.start + other.duration) return false;

        const otherPriority = other.priority ?? 0;

        // Strictly higher priority wins
        if (otherPriority > trackPriority) return true;

        // Equal priority: higher array index = visually "on top" = wins
        if (otherPriority === trackPriority && otherIndex > trackIndex) return true;

        return false;
    });

    return muted ? 0 : baseVolume;
}

/**
 * Convert modified enriched tracks back to cuesheet item updates.
 * Detects changes in timecode, instanceDuration, and priority.
 *
 * @param {Array} tracks - Modified enriched tracks from timeline
 * @param {Array} originalCueSheetItems - Original cuesheet items from the store
 * @param {number} frameRate - Frame rate for timecode conversion (default 24)
 * @returns {Array} Array of updates: [{ id, timecode?, instanceDuration?, priority? }]
 */
export function tracksToCueSheetUpdates(tracks, originalCueSheetItems, frameRate = 24) {
    if (!Array.isArray(tracks) || !Array.isArray(originalCueSheetItems)) {
        console.warn('tracksToCueSheetUpdates: invalid input');
        return [];
    }

    const updates = [];

    tracks.forEach(track => {
        const originalItem = originalCueSheetItems.find(item => item.id === track.id);
        if (!originalItem) {
            console.warn('Track not found in original cuesheet items:', track.id);
            return;
        }

        const update = { id: track.id };
        let hasChanges = false;

        // Check if position (timecode) changed
        const originalStart = timecodeToSeconds(originalItem.timecode || '00:00:00:00', frameRate);
        if (Math.abs(track.start - originalStart) > 0.001) {
            update.timecode = secondsToTimecode(track.start, frameRate);
            hasChanges = true;
        }

        // Check if duration (instanceDuration) changed
        const originalDuration = originalItem.instanceDuration !== undefined
            ? originalItem.instanceDuration / 1000
            : (originalItem.clip?.duration || 0) / 1000;

        if (Math.abs(track.duration - originalDuration) > 0.001) {
            update.instanceDuration = Math.round(track.duration * 1000);
            hasChanges = true;
        }

        // Check if priority changed
        const originalPriority = originalItem.priority ?? 0;
        if ((track.priority ?? 0) !== originalPriority) {
            update.priority = track.priority ?? 0;
            hasChanges = true;
        }

        // Check if startPlayback (trim-in offset) changed
        const originalStartPlayback = originalItem.startPlayback ?? 0;
        if (Math.abs((track.startPlayback ?? 0) - originalStartPlayback) > 0.001) {
            update.startPlayback = track.startPlayback ?? 0;
            hasChanges = true;
        }

        if (hasChanges) {
            updates.push(update);
        }
    });

    return updates;
}
